/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FunctionComponent } from 'react'
import { ScrollView } from 'react-native'
import styled from 'styled-components/native'

import { Loader } from 'ui/components/Loader'
import { ModalDetailedFooter } from 'ui/designSystem/ModalDetailed/ModalDetailedFooter'
import { ModalDetailedHeader } from 'ui/designSystem/ModalDetailed/ModalDetailedHeader'
import { ModalDetailedLayoutProps } from 'ui/designSystem/ModalDetailed/types'
import { Typo } from 'ui/theme'

export const ModalDetailedLayout: FunctionComponent<ModalDetailedLayoutProps> = ({
  title,
  titleId,
  description,
  onClose,
  onGoBack,
  closeAccessibilityLabel,
  backAccessibilityLabel,
  isLoading,
  loadingMessage,
  children,
  hasFixedFooter,
  buttonsDescription,
  primaryButton,
  secondaryButton,
  tertiaryButton,
}) => {
  const isFooterFixed = !isLoading && !!hasFixedFooter

  const footer = isLoading ? null : (
    <ModalDetailedFooter
      buttonsDescription={buttonsDescription}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      tertiaryButton={tertiaryButton}
    />
  )

  return (
    <ModalContainer isFooterFixed={isFooterFixed}>
      <ModalDetailedHeader
        title={title}
        titleId={titleId}
        onClose={onClose}
        onGoBack={onGoBack}
        closeAccessibilityLabel={closeAccessibilityLabel}
        backAccessibilityLabel={backAccessibilityLabel}
      />

      <ContentScrollView
        testID="modalDetailedScrollView"
        tabIndex={0}
        isFooterFixed={isFooterFixed}
        bounces={false}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled
        contentContainerStyle={[
          isLoading && loadingContentStyle,
          isFooterFixed && fixedFooterContentStyle,
        ]}>
        {isLoading ? (
          <Loader message={loadingMessage} />
        ) : (
          <ContentContainer fillAvailableSpace={isFooterFixed}>
            {description ? <Description>{description}</Description> : null}
            <ChildrenContainer fillAvailableSpace={isFooterFixed}>{children}</ChildrenContainer>
          </ContentContainer>
        )}
        {isFooterFixed || !footer ? null : <AdaptiveFooter>{footer}</AdaptiveFooter>}
      </ContentScrollView>

      {isFooterFixed ? footer : null}
    </ModalContainer>
  )
}

const loadingContentStyle = { flexGrow: 1, justifyContent: 'center' as const }
const fixedFooterContentStyle = { flexGrow: 1 }

const ModalContainer = styled.View<{ isFooterFixed: boolean }>(({ theme, isFooterFixed }) => ({
  height: isFooterFixed ? '100%' : undefined,
  width: '100%',
  overflow: 'hidden',
  padding: theme.designSystem.size.spacing.xxl,
  paddingBottom: theme.designSystem.size.spacing.xl,
  backgroundColor: theme.designSystem.color.background.default,
}))

const ContentScrollView = styled(ScrollView)<{ isFooterFixed: boolean }>(({ isFooterFixed }) => ({
  flex: isFooterFixed ? 1 : undefined,
  flexShrink: 1,
}))

const ContentContainer = styled.View<{ fillAvailableSpace: boolean }>(
  ({ theme, fillAvailableSpace }) => ({
    flexGrow: fillAvailableSpace ? 1 : undefined,
    alignItems: 'stretch',
    rowGap: theme.designSystem.size.spacing.l,
  })
)

const ChildrenContainer = styled.View<{ fillAvailableSpace: boolean }>(
  ({ fillAvailableSpace }) => ({
    flexGrow: fillAvailableSpace ? 1 : undefined,
    alignItems: 'stretch',
    width: '100%',
  })
)

const AdaptiveFooter = styled.View(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.xxl,
}))

const Description = styled(Typo.Body)(({ theme }) => ({
  color: theme.designSystem.color.text.default,
  textAlign: 'left',
}))
